import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleTable, Input } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { getOrderColumns } from './tableColumns';
import { mapBackendOrdersToFrontend } from './orderMapper';
import * as OrderApi from "@src/api/OrderApi";
import styles from './Orders.module.scss';
import PageContainer from '@src/components/PageContainer';
const Orders = ({ clinicView }) => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
    const latestOrdersRequestId = useRef(0);
    useEffect(() => () => {
        latestOrdersRequestId.current += 1;
    }, []);
    useEffect(() => {
        const normalizedSearch = searchTerm.trim();
        if (!normalizedSearch) {
            // Fetch immediately when there is no search term, avoiding unnecessary debounce
            fetchOrders('');
            return;
        }
        const timeoutId = window.setTimeout(() => {
            fetchOrders(normalizedSearch);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);
    const fetchOrders = async (search = '') => {
        const requestId = ++latestOrdersRequestId.current;
        const normalizedSearch = search.trim();
        try {
            setLoading(true);
            const response = await OrderApi.getOrders({ search: normalizedSearch || undefined });
            if (!response.ok) {
                throw new Error('Failed to fetch orders');
            }
            // const data = await response.json();
            const data = response.body;
            const backendOrders = data.results || data;
            const mappedOrders = mapBackendOrdersToFrontend(Array.isArray(backendOrders) ? backendOrders : [backendOrders]);
            if (requestId !== latestOrdersRequestId.current) {
                return;
            }
            setOrders(mappedOrders);
        }
        catch (error) {
            if (requestId !== latestOrdersRequestId.current) {
                return;
            }
            console.error('Error fetching orders:', error);
            setOrders([]);
        }
        finally {
            if (requestId === latestOrdersRequestId.current) {
                setLoading(false);
            }
        }
    };
    const fetchPendingOrdersCount = async () => {
        try {
            const response = await OrderApi.getOrders({ status: 'pending' });
            if (!response.ok) {
                throw new Error('Failed to fetch pending orders');
            }
            const data = response.body;
            if (typeof data?.count === 'number') {
                setPendingOrdersCount(data.count);
                return;
            }
            const pendingList = data?.results || data;
            setPendingOrdersCount(Array.isArray(pendingList) ? pendingList.length : 0);
        }
        catch (error) {
            console.error('Error fetching pending orders count:', error);
            setPendingOrdersCount(0);
        }
    };
    const handleView = (order) => {
        navigate((clinicView ? "" : "/web-admin") + `/orders/${order.uuid || order.id}`);
    };
    const handleDelete = async (order) => {
        if (window.confirm(`Are you sure you want to delete order ${order.order_number}?`)) {
            try {
                const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                });
                if (!response.ok) {
                    throw new Error('Failed to delete order');
                }
                await fetchOrders(searchTerm);
            }
            catch (error) {
                console.error('Error deleting order:', error);
                alert('Failed to delete order');
            }
        }
    };
    const handleUpdateStatus = async (order, newStatus) => {
        try {
            const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!response.ok) {
                throw new Error('Failed to update order status');
            }
            await fetchOrders(searchTerm);
        }
        catch (error) {
            console.error('Error updating order status:', error);
            alert('Failed to update order status');
        }
    };
    const handleTrackShipment = (order) => {
        if (order.tracking_number) {
            window.open(`https://www.fedex.com/fedextrack/?trknbr=${order.tracking_number}`, '_blank');
        }
        else {
            alert('No tracking number available for this order');
        }
    };
    const handleMarkReturned = async (order) => {
        if (window.confirm(`Mark order ${order.order_number} as returned?`)) {
            try {
                const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                    body: JSON.stringify({
                        status: 'cancelled',
                        notes: `${order.notes ? order.notes + '\n' : ''}Order marked as returned on ${new Date().toLocaleString()}`,
                    }),
                });
                if (!response.ok) {
                    throw new Error('Failed to mark order as returned');
                }
                await fetchOrders(searchTerm);
            }
            catch (error) {
                console.error('Error marking order as returned:', error);
                alert('Failed to mark order as returned');
            }
        }
    };
    const handleMarkRefunded = async (order) => {
        if (window.confirm(`Mark order ${order.order_number} as refunded?`)) {
            try {
                const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                    body: JSON.stringify({
                        payment_status: 'refunded',
                        notes: `${order.notes ? order.notes + '\n' : ''}Order marked as refunded on ${new Date().toLocaleString()}`,
                    }),
                });
                if (!response.ok) {
                    throw new Error('Failed to mark order as refunded');
                }
                await fetchOrders(searchTerm);
            }
            catch (error) {
                console.error('Error marking order as refunded:', error);
                alert('Failed to mark order as refunded');
            }
        }
    };
    const handleExport = async () => {
        try {
            const response = await fetch('/api/v1/orders/export/', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to export orders');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `order_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Error exporting orders:', error);
            alert('Failed to export orders');
        }
    };
    const handleProcessDispatch = async () => {
        const confirmed = window.confirm('Process all pending orders for dispatch and send the dispatch email report?');
        if (!confirmed) {
            return;
        }
        try {
            const csrfToken = document.cookie
                .split(';')
                .map((c) => c.trim())
                .find((row) => row.startsWith('csrftoken='))
                ?.split('=')[1] || '';
            const response = await fetch('/api/v1/orders/process-dispatch/', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    'X-CSRFToken': csrfToken,
                },
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Failed to process dispatch');
            }
            const processedCount = typeof data.processed_count === 'number' ? data.processed_count : null;
            const successMessage = processedCount === null
                ? (data.message || 'Order dispatch process completed successfully.')
                : `Order dispatch process completed successfully. Processed ${processedCount} pending order${processedCount === 1 ? '' : 's'}.`;
            alert(successMessage);
            await fetchOrders(searchTerm);
            await fetchPendingOrdersCount();
        }
        catch (error) {
            console.error('Error processing order dispatch:', error);
            alert('Failed to process order dispatch');
        }
    };
    useEffect(() => {
        if (!clinicView) {
            fetchPendingOrdersCount();
        }
    }, [clinicView]);
    const columns = getOrderColumns(handleView, handleDelete, handleUpdateStatus, handleTrackShipment, handleMarkReturned, handleMarkRefunded);
    const filterOptions = [
        { label: 'Submitted', value: 'submitted' },
        { label: 'Shipped', value: 'shipped' },
        { label: 'Delivered', value: 'delivered' },
        { label: 'Cancelled', value: 'cancelled' },
    ];
    const PageWrapper = clinicView ? PageContainer : AdminLayout;
    return (_jsx(PageWrapper, { children: _jsxs("div", { className: styles.container, children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by order #, clinic, email, or tracking", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }), _jsx(SimpleTable, { columns: columns, data: orders, rowHeight: "60px", tableHeading: "Orders", titleStyle: {
                                color: '#000',
                                fontFamily: 'Nunito',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '28px',
                            }, hideTopBorder: true, filterColumn: "status", filterLabel: "Show All Statuses", enablePagination: true, paginationText: "Orders per page", onRowClick: (order) => handleView(order), customButton: _jsxs("div", { className: styles.actionsRow, children: [!clinicView && (pendingOrdersCount > 0 ? (_jsxs("button", { className: styles.processDispatchButton, onClick: handleProcessDispatch, children: ["Process ", pendingOrdersCount, " Pending Order", pendingOrdersCount === 1 ? '' : 's', " for Dispatch"] })) : null), _jsxs("button", { className: styles.exportButton, onClick: handleExport, children: ["Export", _jsx("i", { className: "fas fa-download" })] })] }) })] })] }) }));
};
export default Orders;
